/**
 * The master editor's General tab (Phase 14): identity + campaign, the
 * shared defaults, and the Hour Optimizer — no schedule, no slots: those
 * belong to each container that follows it. A master has no List groups
 * tab, so the list verdict, A list fails and scaling sit here (20/10/2026 —
 * user: "a list fails should be inside list verdicts please").
 * For Media Devoted | Created By Ciro Autiero
 */
import { FC } from 'react';
import { Crown, Hash } from 'lucide-react';

import { StepCard, StepCardGroup } from '@/components/ui';

import type { DropGenPlanDetail } from '../../../drop-generator.types';
import { ContainerStep } from '../general/ContainerStep';
import { HourOptimizerStep } from '../general/optimizer/HourOptimizerStep';
import type { ContainerEditor } from '../state/useContainerEditor';
import { TAB_CHROME } from '../tabs/tab-chrome';
import { HitAgainCard, ListVerdictCard, ScalingCard, VerdictHome } from '../verdicts/cards';
import { MasterDefaultsStep } from './MasterDefaultsStep';

interface Props {
  editor: ContainerEditor;
  plan: DropGenPlanDetail;
  containerId: string | null;
  campaignLocked?: boolean;
}

/** A master config plans no drops of its own. */
const NO_SLOTS: number[] = [];

const MasterSteps: FC<Props> = ({ editor, plan, containerId, campaignLocked = false }) => {
  const { draft } = editor;
  const name = draft.name.trim();
  return (
    <StepCardGroup>
      <StepCard index={1} icon={Crown} title="Master config" summary={name ? `${name} · campaign ${draft.campaignWiserId ?? 'unset'}` : 'name and campaign not set'}>
        <ContainerStep editor={editor} countryKey={plan.countryKey} mode="master" campaignLocked={campaignLocked} />
      </StepCard>
      <StepCard index={2} icon={Hash} title="Shared defaults" summary={`${draft.defaultAmount.toLocaleString('en-US')}/drop`}>
        <MasterDefaultsStep editor={editor} />
      </StepCard>
      <HourOptimizerStep index={3} editor={editor} countryKey={plan.countryKey} startMin={null} endMin={null} slotMinutes={NO_SLOTS} windowStartMin={plan.windowStartMin} windowEndMin={plan.windowEndMin} />
      <HitAgainCard index={4} editor={editor} plan={plan} containerId={containerId} />
    </StepCardGroup>
  );
};

export const MasterGeneralTab: FC<Props> = ({ editor, plan, containerId, campaignLocked = false }) => (
  <div className="flex flex-col gap-4">
    <MasterSteps editor={editor} plan={plan} containerId={containerId} campaignLocked={campaignLocked} />
    <VerdictHome title="List verdicts" hint="These rules judge the lists. Followers see them on List groups too." titleClass={TAB_CHROME.groups.heading}>
      <ListVerdictCard index={1} editor={editor} plan={plan} containerId={containerId} />
      <ScalingCard index={2} editor={editor} plan={plan} containerId={containerId} />
    </VerdictHome>
  </div>
);
